import { Link } from '@tanstack/react-router';
import { Megaphone } from 'lucide-react';
import { EtainablSpinner } from '@/components/etainabl-spinner';
import { EmptyState } from '@/components/ui/empty-state';
import { ViewingAsBanner } from '@/features/view-as/ViewingAsBanner';
import { useReleaseNotes } from '@/queries/releaseNotes';
import { cn } from '@/lib/utils';
import type { EditionSummary } from '@/types/releaseNotes';
import { changeCount, periodLabel } from './format';
import { useSeenEditions } from './useSeenEditions';

function EditionCard({ edition, unread, asUser }: { edition: EditionSummary; unread: boolean; asUser?: string }) {
  return (
    <li>
      <Link
        to="/release-notes/$key"
        params={{ key: edition.key }}
        search={{ asUser } as never}
        className="group block rounded-etn-card border border-etn-border-hairline bg-etn-surface px-5 py-4 transition-colors duration-150 hover:bg-etn-row-hover focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-etn-accent"
        data-testid="release-note-card"
      >
        <div className="flex items-baseline justify-between gap-4">
          <h2 className="flex items-center gap-2 text-base font-semibold text-etn-text group-hover:underline">
            {unread && <span className="h-2 w-2 shrink-0 rounded-full bg-etn-accent" aria-hidden />}
            {periodLabel(edition)}
            {unread && <span className="sr-only">(unread)</span>}
          </h2>
          <span className="shrink-0 text-etn-micro font-semibold text-etn-text-soft">{changeCount(edition.itemCount)}</span>
        </div>
        {edition.highlightTitles.length > 0 && (
          <ul className={cn('mt-2 space-y-1', unread && 'pl-4')}>
            {edition.highlightTitles.map(title => (
              <li key={title} className="flex gap-2 text-etn-cell text-etn-text-cell">
                <span className="mt-[0.55em] h-1 w-1 shrink-0 rounded-full bg-etn-text-soft" aria-hidden />
                {title}
              </li>
            ))}
          </ul>
        )}
      </Link>
    </li>
  );
}

export function ReleaseNotesListView({ asUser }: { asUser?: string }) {
  const { data, isLoading } = useReleaseNotes(asUser);
  const { isSeen } = useSeenEditions();
  const editions = data?.data ?? [];
  // Monthly round-ups cover the months before weekly editions began, so they sit together after them.
  const weekly = editions.filter(e => e.kind === 'weekly');
  const monthly = editions.filter(e => e.kind === 'backfill');

  return (
    <>
      {data?.viewingAs && <ViewingAsBanner name={data.viewingAs.name} companyName={data.viewingAs.companyName} />}
      <div className="container mx-auto max-w-3xl space-y-8 p-6">
        <header className="space-y-1">
          <h1 className="text-2xl font-semibold text-etn-text">Release notes</h1>
          <p className="text-etn-cell text-etn-text-label">What changed in Etainabl, every Friday.</p>
        </header>

        {isLoading && (
          <div className="flex justify-center py-8">
            <EtainablSpinner size="sm" />
          </div>
        )}

        {!isLoading && editions.length === 0 && (
          <EmptyState icon={<Megaphone />} title="No release notes yet" body="New editions appear here every Friday afternoon." />
        )}

        {weekly.length > 0 && (
          <ul className="space-y-3" aria-label="Weekly editions">
            {weekly.map(edition => (
              <EditionCard key={edition.key} edition={edition} unread={!isSeen(edition.key)} asUser={asUser} />
            ))}
          </ul>
        )}

        {monthly.length > 0 && (
          <section aria-labelledby="release-notes-monthly" className="space-y-3">
            <h2 id="release-notes-monthly" className="text-etn-micro font-semibold uppercase tracking-[0.05em] text-etn-text-label">
              Monthly round-ups
            </h2>
            <ul className="space-y-3">
              {monthly.map(edition => (
                <EditionCard key={edition.key} edition={edition} unread={!isSeen(edition.key)} asUser={asUser} />
              ))}
            </ul>
          </section>
        )}
      </div>
    </>
  );
}
